/**
 * Lista de membros do servidor.
 *
 * Agrupada por cargo destacado (`hoist`), com quem está offline no fim e mais
 * apagado. Ver quem está disponível agora é a informação que importa; a lista
 * completa é secundária.
 */

import { useMemo, useState } from 'react'
import { MonitorUp, Search, Video } from 'lucide-react'
import type { GuildMember, PresenceStatus, Role } from '@nexus/shared'
import { cn } from '@/lib/cn'
import { useAppStore } from '@/store/app'
import { useGuildMembers } from '@/hooks/selectors'
import { Avatar, Input } from '@/components/ui'
import { UserContextMenu } from '@/components/layout/UserContextMenu'
import { NicknamePrompt } from '@/components/layout/NicknamePrompt'

interface Group {
  key: string
  label: string
  color: string | null
  members: GuildMember[]
}

export function MemberList({ guildId }: { guildId: string }) {
  const roles = useAppStore((s) => s.roles)
  const presences = useAppStore((s) => s.presences)
  const voiceStates = useAppStore((s) => s.voiceStates)

  const [search, setSearch] = useState('')
  const [menu, setMenu] = useState<{ x: number; y: number; member: GuildMember } | null>(null)
  const [apelidoDe, setApelidoDe] = useState<GuildMember | null>(null)

  const members = useGuildMembers(guildId)

  const groups = useMemo<Group[]>(() => {

    const filtered = search.trim()
      ? members.filter((m) => {
          const term = search.toLowerCase()
          return (
            m.user.displayName.toLowerCase().includes(term) ||
            m.user.username.toLowerCase().includes(term) ||
            (m.nickname?.toLowerCase().includes(term) ?? false)
          )
        })
      : members

    const hoisted = Object.values(roles)
      .filter((r) => r.guildId === guildId && r.hoist && !r.isEveryone)
      .sort((a, b) => b.position - a.position)

    const online: GuildMember[] = []
    const offline: GuildMember[] = []
    const byRole = new Map<string, GuildMember[]>()

    for (const member of filtered) {
      const status = presences[member.userId]?.status ?? 'offline'

      if (status === 'offline') {
        offline.push(member)
        continue
      }

      // Cai no primeiro cargo destacado que o membro tem, do mais alto ao mais
      // baixo. Sem nenhum, vai para o grupo geral de online.
      const role = hoisted.find((r) => member.roleIds.includes(r.id))
      if (role) {
        const list = byRole.get(role.id)
        if (list) list.push(member)
        else byRole.set(role.id, [member])
      } else {
        online.push(member)
      }
    }

    const result: Group[] = []

    for (const role of hoisted) {
      const list = byRole.get(role.id)
      if (list && list.length > 0) {
        result.push({ key: role.id, label: role.name, color: role.color, members: list })
      }
    }
    if (online.length > 0) {
      result.push({ key: 'online', label: 'Online', color: null, members: online })
    }
    if (offline.length > 0) {
      result.push({ key: 'offline', label: 'Offline', color: null, members: offline })
    }

    return result
  }, [guildId, members, presences, roles, search])

  return (
    <aside aria-label="Membros" className="flex w-members shrink-0 flex-col bg-surface-sunken">
      <div className="p-2.5">
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-content-muted" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar membros"
            className="h-8 pl-8 text-[13px]"
          />
        </div>
      </div>

      <div className="scroll-region flex-1 px-2 pb-3">
        {groups.map((group) => (
          <section key={group.key} className="mb-4">
            <h3
              className="mb-1 px-2 text-[11px] font-bold tracking-wider uppercase"
              style={{ color: group.color ?? 'var(--color-content-muted)' }}
            >
              {group.label} <span className="opacity-70">— {group.members.length}</span>
            </h3>

            {group.members.map((member) => (
              <MemberRow
                key={member.userId}
                member={member}
                status={presences[member.userId]?.status ?? 'offline'}
                customStatus={presences[member.userId]?.customStatus ?? null}
                sharing={voiceStates[member.userId]?.screenSharing ?? false}
                camera={voiceStates[member.userId]?.cameraOn ?? false}
                roles={member.roleIds.map((id) => roles[id]).filter((r): r is Role => Boolean(r))}
                onContextMenu={(x, y) => setMenu({ x, y, member })}
              />
            ))}
          </section>
        ))}

        {groups.length === 0 ? (
          <p className="px-2 py-6 text-center text-[13px] text-content-muted">
            Nenhum membro encontrado
          </p>
        ) : null}
      </div>

      {menu ? (
        <UserContextMenu
          userId={menu.member.userId}
          guildId={guildId}
          posicao={{ x: menu.x, y: menu.y }}
          onAlterarApelido={setApelidoDe}
          onClose={() => setMenu(null)}
        />
      ) : null}

      <NicknamePrompt
        membro={apelidoDe}
        guildId={guildId}
        onClose={() => setApelidoDe(null)}
      />
    </aside>
  )
}

function MemberRow({
  member,
  status,
  customStatus,
  sharing,
  camera,
  roles,
  onContextMenu,
}: {
  member: GuildMember
  status: PresenceStatus
  customStatus: string | null
  sharing: boolean
  camera: boolean
  roles: Role[]
  onContextMenu: (x: number, y: number) => void
}) {
  const name = member.nickname ?? member.user.displayName
  const offline = status === 'offline'

  const color = roles
    .filter((r) => r.color)
    .sort((a, b) => b.position - a.position)[0]?.color

  return (
    <button
      type="button"
      onContextMenu={(e) => {
        e.preventDefault()
        onContextMenu(e.clientX, e.clientY)
      }}
      onClick={(e) => onContextMenu(e.clientX, e.clientY)}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-md px-2 py-1 text-left transition-colors',
        'hover:bg-surface-hover',
        offline && 'opacity-40 hover:opacity-100',
      )}
    >
      <Avatar
        userId={member.userId}
        name={name}
        src={member.user.avatarUrl}
        size="sm"
        status={status}
      />

      <div className="min-w-0 flex-1">
        <p
          className="truncate text-[14px] font-medium text-content-primary"
          style={color ? { color } : undefined}
        >
          {name}
        </p>
        {customStatus ? (
          <p className="truncate text-[11px] text-content-muted">{customStatus}</p>
        ) : null}
      </div>

      <div className="flex shrink-0 items-center gap-1 text-content-muted">
        {sharing ? <MonitorUp className="size-3.5 text-success" /> : null}
        {camera ? <Video className="size-3.5" /> : null}
      </div>
    </button>
  )
}
